<template>
  <div>
    <v-card>
      <v-card-title>Configuration des sources</v-card-title>
      <v-row>
        <v-col>
          <v-data-table
            class="ml-6 mr-6"
            :page="pagination.options.page"
            :pageCount="pagination.numberOfPages"
            :headers="headers"
            :items="pagination.items"
            :options.sync="pagination.options"
            :server-items-length="pagination.total"
            :loading="page.loading"
            :disable-filtering="true"
            :disable-sort="true"
            :footer-props="{
              'items-per-page-options': [10, 25, 50],
            }"
            :items-per-page="pagination.options.itemsPerPage"
            :item-class="rowClass"
          >
            <template v-slot:item.is_active="{ item }">
              <v-switch
                :input-value="item.is_active"
                :loading="page.togglingId === item.id"
                :disabled="page.togglingId === item.id"
                class="mt-0 pt-0"
                inset
                dense
                hide-details
                @change="toggleActive(item, $event)"
              />
            </template>
            <template v-slot:item.created="{ item }">
              {{ formatCreated(item.created) }}
            </template>
            <template v-slot:item.entry="{ item }">
              <div class="truncateCell">
                {{ item.entry }}
              </div>
            </template>
            <template v-slot:item.media_type="{ item }">
              {{ mediaTypeLabels[item.media_type] || item.media_type }}
            </template>
            <template v-slot:item.edit="{ item }">
              <div>
                <v-btn
                  class="mx-2"
                  elevation="0"
                  fab
                  small
                  @click="editConfig(item)"
                >
                  <v-icon color="primary">
                    {{ icons.mdiPencil }}
                  </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  elevation="0"
                  fab
                  small
                  @click="removeConfig(item)"
                >
                  <v-icon color="secondary">
                    {{ icons.mdiTrashCan }}
                  </v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="dialogModel.dialog" max-width="600px">
      <v-card>
        <v-card-title>Modifier la configuration</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="page.configToUpdate.name"
                label="Nom de la config"
                outlined
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="page.configToUpdate.news_radar_source.name"
                label="Source"
                outlined
                hide-details
                disabled
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="page.configToUpdate.site.name"
                label="Site"
                outlined
                hide-details
                disabled
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                outlined
                v-model="page.configToUpdate.entry"
                :items="[]"
                :menu-props="{ maxHeight: '400', zIndex: zIndex.modale }"
                label="Entrée de la config"
                allow-overflow
                hide-selected
                hide-no-data
                :search-input.sync="page.searchEntry"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                outlined
                hide-details
                v-model="page.configToUpdate.media_type"
                :items="mediaTypes"
                :disabled="!editedSourceCanCarryVideo"
                :menu-props="{ zIndex: zIndex.modale }"
                label="Support"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row class="w-100" justify="center" align="center">
            <v-col cols="auto">
              <v-btn color="primary" @click="updateConfig">Enregistrer</v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn color="secondary" @click="cancelUpdateConfig"
                >Annuler</v-btn
              >
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="page.showSuccessMessage"
      color="success"
      timeout="3000"
      top
    >
      Modification enregistrée
    </v-snackbar>
  </div>
</template>

<script>
import { computed, onMounted, reactive, watch } from "@vue/composition-api";
import { mdiPencil, mdiTrashCan } from "@mdi/js";

import validateConfig from "@/components/news-radar/config/helpers/validateConfig";
import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import {
  NEWS_RADAR_MEDIA_TYPE_LABELS,
  NEWS_RADAR_MEDIA_TYPES,
  NEWS_RADAR_VIDEO_SOURCES,
  zIndex,
} from "@/utils/constants";
import { truncate } from "@/utils/formatting";

export default {
  name: "ConfigTable",
  setup(props, { emit }) {
    const { axiosGet, axiosPost, axiosPatch, axiosDelete } = useAxios();
    const { store } = useProxy();

    const page = reactive({
      configToUpdate: {
        name: "",
        entry: "",
        media_type: "written",
        news_radar_source: { name: "" },
        site: { name: "" },
      },
      forceCall: false,
      loading: false,
      isOptionsInitialized: false,
      searchEntry: "",
      showSuccessMessage: false,
      togglingId: null,
    });

    const pagination = reactive({
      numberOfPages: 0,
      total: 0,
      items: [],
      options: {
        page: 1,
        itemsPerPage: 50,
      },
    });

    const dialogModel = reactive({
      dialog: false,
    });

    // =>  METHODS
    const formatResult = (items) =>
      items.map((item) => ({
        ...item,
        ...(item.news_radar_source.name === "Marfeel"
          ? { entry: item.extra }
          : {}),
      }));

    const getConfigs = async () => {
      if ((applyFilters.value || page.forceCall) && !page.loading) {
        try {
          page.loading = true;

          const {
            data: { total, items },
          } = await axiosGet("/news-radar-configs/all", {
            ...(site.value ? { site_id: site.value } : {}),
            ...(sources.value ? { source_ids: sources.value } : {}),
            ...(origins.value ? { origins: origins.value.join(",") } : {}),
            ...(configs.value ? { config_ids: configs.value.join(",") } : {}),
            page: pagination.options.page,
            limit: pagination.options.itemsPerPage,
          });

          pagination.total = total;
          pagination.items = formatResult(items);
        } catch (error) {
          console.error("error while fetching data");
        }

        page.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }

      page.forceCall = false;
    };

    const cancelUpdateConfig = async () => {
      dialogModel.dialog = false;
    };

    const editConfig = (item) => {
      page.searchEntry = "";
      page.configToUpdate = item;
      dialogModel.dialog = true;
    };

    const updateConfig = async () => {
      const validation = validateConfig(
        page.configToUpdate,
        page.configToUpdate.news_radar_source.name
      );
      if (validation !== true) {
        emit("warning", validation);

        return;
      }

      // `media_type` DOIT partir : l'enregistrement est un upsert dont la clé
      // est (site, source, entrée, support). L'omettre ferait retomber la
      // config sur « écrit » côté API, donc créerait une seconde ligne au lieu
      // de modifier la config vidéo qu'on vient d'éditer.
      const data = {
        name: page.configToUpdate.name,
        entry: page.configToUpdate.entry,
        media_type: page.configToUpdate.media_type,
        news_radar_source_id: page.configToUpdate.news_radar_source.id,
        site_id: page.configToUpdate.site.id,
      };

      try {
        await axiosPost(`/news-radar-configs/`, data);
      } catch (error) {
        console.error("Error saving UTM:", error);
        emit("error");
      }
      dialogModel.dialog = false;
      page.showSuccessMessage = true;
      await refreshList();

      emit("saved");
    };

    // Désactiver = sortir la config du crawl et masquer ses articles partout,
    // sans la supprimer : elle reste réactivable ici.
    const toggleActive = async (item, isActive) => {
      page.togglingId = item.id;
      try {
        await axiosPatch(`/news-radar-configs/${item.id}/active`, {
          is_active: !!isActive,
        });
        item.is_active = !!isActive;
      } catch (error) {
        console.error("error while toggling config", error);
        emit("error");
      }
      page.togglingId = null;
    };

    // `created` est NULL pour les configs antérieures à la migration du
    // 2026-09-15 : on ne connaît pas leur date d'ajout.
    const formatCreated = (created) =>
      created ? new Date(created).toLocaleDateString("fr-FR") : "—";

    const rowClass = (item) => (item.is_active ? "" : "config-row--inactive");

    const removeConfig = async (item) => {
      await axiosDelete(`/news-radar-configs/${item.id}`);
      // TODO -> Confirmation
      await refreshList();
    };

    const refreshList = async () => {
      page.forceCall = true;
      await getConfigs();
    };

    // =>  COMPUTED
    const editedSourceCanCarryVideo = computed(() =>
      NEWS_RADAR_VIDEO_SOURCES.includes(
        page.configToUpdate.news_radar_source?.name
      )
    );

    const applyFilters = computed(
      () => store.getters["common/getApplyFilters"]
    );
    const site = computed(() => store.getters["newsRadar/getSites"]);
    const sources = computed(() => store.getters["newsRadar/getSources"]);
    const origins = computed(() => store.getters["newsRadar/getOrigins"]);
    const configs = computed(() => store.getters["newsRadar/getConfigs"]);

    // =>  WATCH
    watch(applyFilters, async () => {
      pagination.options.page = 1;
      await getConfigs();
    });

    watch(
      () => pagination.options,
      () => {
        if (page.isOptionsInitialized && !applyFilters.value) {
          // Not on initialization : wait for a real user change
          page.forceCall = true;
        }

        page.isOptionsInitialized = true;
        getConfigs();
      }
    );

    watch(
      () => page.searchEntry,
      (newValue) => {
        page.configToUpdate.entry = newValue;
      }
    );

    // =>  HOOKS
    onMounted(async () => {
      await getConfigs();
    });

    // =>  DATA
    return {
      cancelUpdateConfig,
      dialogModel,
      editConfig,
      editedSourceCanCarryVideo,
      formatCreated,
      headers: [
        { text: "Nom", value: "name" },
        { text: "Entrée", value: "entry" },
        { text: "Site", value: "site.name" },
        { text: "Source", value: "news_radar_source.name" },
        { text: "Support", value: "media_type", width: "90px" },
        { text: "Active", value: "is_active" },
        { text: "Ajoutée le", value: "created" },
        { text: "Actions", value: "edit" },
      ],
      icons: {
        mdiPencil,
        mdiTrashCan,
      },
      mediaTypeLabels: NEWS_RADAR_MEDIA_TYPE_LABELS,
      mediaTypes: NEWS_RADAR_MEDIA_TYPES,
      page,
      pagination,
      removeConfig,
      rowClass,
      toggleActive,
      truncate,
      updateConfig,
      zIndex,
    };
  },
};
</script>

<style scoped>
::v-deep .config-row--inactive td {
  opacity: 0.5;
}

.truncateCell {
  width: 10rem;
  overflow: hidden;
  cursor: pointer;
  overflow-x: scroll;
  white-space: nowrap;
}
</style>
